import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { Icon } from '@tabler/icons-react'
import { useRouter } from 'next/router'

import { useNavigation } from '@/context/navigationContext'
import { retainBreadcrumbs } from '@/utils/retainBreadcrumbs'

export type Breadcrumb = {
  text: string
  icon?: Icon
  path?: string
}

const BreadcrumbStateContext = createContext<Breadcrumb[]>([])
const BreadcrumbUpdaterContext = createContext<(b: Breadcrumb[]) => void>(
  () => {}
)

export const BreadcrumbProvider = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])
  const { currentNavItem } = useNavigation()
  const { events, asPath } = useRouter()

  const smartbreadcrumbs: Breadcrumb[] = useMemo(() => {
    const path = breadcrumbs?.length > 0 ? currentNavItem?.path : undefined

    const defaultBreadcrumbs = currentNavItem
      ? [{ text: currentNavItem.title, icon: currentNavItem.icon, path }]
      : []

    return [...defaultBreadcrumbs, ...breadcrumbs]
  }, [breadcrumbs, currentNavItem])

  useEffect(() => {
    const clearCrumbs = (toUrl: string) => {
      if (toUrl === asPath) return // prevent clearing on initial page load

      if (toUrl === currentNavItem?.path) {
        setBreadcrumbs((crumbs) => (crumbs.length ? [] : crumbs)) // clear crumbs when going back to root to reset
      } else {
        setBreadcrumbs((crumbs) => {
          return retainBreadcrumbs(asPath, toUrl, currentNavItem?.path, crumbs)
        })
      }
    }
    events.on('routeChangeStart', clearCrumbs)

    return () => events.off('routeChangeStart', clearCrumbs)
  }, [events, asPath, currentNavItem])

  return (
    <BreadcrumbStateContext.Provider value={smartbreadcrumbs}>
      <BreadcrumbUpdaterContext.Provider value={setBreadcrumbs}>
        {children}
      </BreadcrumbUpdaterContext.Provider>
    </BreadcrumbStateContext.Provider>
  )
}

export const useBreadcrumbs = () => {
  const breadcrumbs = useContext(BreadcrumbStateContext)

  return breadcrumbs
}

export const useSetBreadcrumbs = (staticBreadCrumbs?: Breadcrumb[]) => {
  const setBreadcrumbs = useContext(BreadcrumbUpdaterContext)

  useEffect(() => {
    if (staticBreadCrumbs?.length) {
      setBreadcrumbs(staticBreadCrumbs)
    }
  }, [staticBreadCrumbs, setBreadcrumbs])

  return setBreadcrumbs
}
